import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useModalDialog } from "@/shared/a11y/useModalDialog";
import { BrandMark } from "@/shared/brand/BrandMark";
import { normalizePublicWebLang, publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import {
  loadPreferences,
  onPreferencesChanged,
  savePreferences,
  type WebPreferences,
} from "@/shared/preferences/store";
import {
  AUDIO_PLAYBACK_SPEED_OPTIONS,
  downloadCurrentAudio,
  getAudioPlaybackState,
  onAudioPlaybackChanged,
  onAudioPlaybackTimelineChanged,
  pauseAudioPlayback,
  retryDeviceSpeechPlayback,
  resumeAudioPlayback,
  seekAudioPlaybackBy,
  seekAudioPlaybackToFraction,
  setAudioPlaybackRate,
  shouldShowAudioUpgradeCta,
  stopAudioPlayback,
  type AudioPlaybackState,
} from "./briefing-audio";
import { audioVoiceOptionsForLanguage, compatibleAudioVoiceId } from "./audio-voice-policy.mjs";
import {
  continueWithDailyDefaultEditionAudio,
  dismissDailyDefaultEditionAudio,
  trackDailyDefaultEditionSubscribe,
} from "./daily-default-edition-audio";
import "./GlobalAudioPlayer.css";

type PlayerIconName =
  | "back"
  | "close"
  | "download"
  | "expand"
  | "forward"
  | "pause"
  | "play"
  | "stop"
  | "voice";

function PlayerIcon({ name }: { name: PlayerIconName }) {
  const line = {
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 1.9,
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {name === "play" ? <path fill="currentColor" d="M8 5.2v13.6L19 12 8 5.2Z" /> : null}
      {name === "pause" ? (
        <>
          <rect x="6.5" y="5" width="4" height="14" rx="1" fill="currentColor" />
          <rect x="13.5" y="5" width="4" height="14" rx="1" fill="currentColor" />
        </>
      ) : null}
      {name === "close" ? (
        <>
          <path {...line} d="m6.5 6.5 11 11" />
          <path {...line} d="m17.5 6.5-11 11" />
        </>
      ) : null}
      {name === "expand" ? (
        <>
          <path {...line} d="M8.5 4.5h-4v4M4.8 4.8l5.1 5.1" />
          <path {...line} d="M15.5 19.5h4v-4M19.2 19.2l-5.1-5.1" />
        </>
      ) : null}
      {name === "download" ? (
        <>
          <path {...line} d="M12 4.5v10M8.2 11l3.8 3.8 3.8-3.8" />
          <path {...line} d="M5 19.5h14" />
        </>
      ) : null}
      {name === "stop" ? (
        <rect x="6.5" y="6.5" width="11" height="11" rx="2" fill="currentColor" />
      ) : null}
      {name === "voice" ? (
        <>
          <path {...line} d="M9 7.5a3 3 0 0 1 6 0v4a3 3 0 0 1-6 0v-4Z" />
          <path {...line} d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v3M9 20h6" />
        </>
      ) : null}
      {name === "back" || name === "forward" ? (
        <>
          <path
            {...line}
            d={
              name === "back"
                ? "M8.2 7.2H4.5V3.5M4.8 7A8 8 0 1 1 4 14"
                : "M15.8 7.2h3.7V3.5M19.2 7A8 8 0 1 0 20 14"
            }
          />
          <text
            x="12"
            y="15"
            textAnchor="middle"
            fill="currentColor"
            fontSize="7.5"
            fontWeight="850"
          >
            15
          </text>
        </>
      ) : null}
    </svg>
  );
}

function AudioWaveform({ active, compact = false }: { active: boolean; compact?: boolean }) {
  return (
    <div
      className={`global-audio-waveform${active ? " is-active" : ""}${compact ? " is-compact" : ""}`}
      aria-hidden="true"
    >
      {Array.from({ length: 28 }, (_, index) => (
        <i key={index} />
      ))}
    </div>
  );
}

const AUDIO_NUMBER_FORMATTERS = new Map<
  string,
  { natural: Intl.NumberFormat; padded: Intl.NumberFormat; rate: Intl.NumberFormat }
>();

function audioNumberFormatters(lang: string) {
  const locale = normalizePublicWebLang(lang);
  const existing = AUDIO_NUMBER_FORMATTERS.get(locale);
  if (existing) return existing;
  const formatters = {
    natural: new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      useGrouping: false,
    }),
    padded: new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      minimumIntegerDigits: 2,
      useGrouping: false,
    }),
    rate: new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
      useGrouping: false,
    }),
  };
  AUDIO_NUMBER_FORMATTERS.set(locale, formatters);
  return formatters;
}

function formatTime(milliseconds = 0, lang = "en"): string {
  const { natural, padded } = audioNumberFormatters(lang);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return `${natural.format(0)}:${padded.format(0)}`;
  }
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${natural.format(hours)}:${padded.format(minutes)}:${padded.format(seconds)}`
    : `${natural.format(minutes)}:${padded.format(seconds)}`;
}

function rateLabel(rate: number, lang: string): string {
  return `${audioNumberFormatters(lang).rate.format(rate)}×`;
}

function playbackProgress(state: AudioPlaybackState): number {
  const duration = state.durationMs || 0;
  return duration > 0 ? Math.min(1, Math.max(0, (state.positionMs || 0) / duration)) : 0;
}

const RANGE_KEYBOARD_KEYS = new Set([
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
]);

function AudioScrubber({
  copy,
  durationKnown,
  lang,
  progress,
  state,
}: {
  copy: ReturnType<typeof publicWebCopyForLang>;
  durationKnown: boolean;
  lang: string;
  progress: number;
  state: AudioPlaybackState;
}) {
  const [draftValue, setDraftValue] = useState<number | null>(null);
  const pointerActive = useRef(false);
  const keyboardActive = useRef(false);
  const pendingDeviceSeek = useRef<number | null>(null);
  const committedValue = Math.round(progress * 1000);
  const displayedValue = draftValue ?? committedValue;
  const displayedProgress = displayedValue / 1000;
  const displayedPositionMs = (state.durationMs || 0) * displayedProgress;

  useEffect(() => {
    setDraftValue(null);
    pendingDeviceSeek.current = null;
  }, [state.durationMs, state.key]);

  function commit(value: number) {
    pendingDeviceSeek.current = null;
    seekAudioPlaybackToFraction(Math.min(1, Math.max(0, value / 1000)));
    setDraftValue(null);
  }

  return (
    <input
      type="range"
      min="0"
      max="1000"
      value={displayedValue}
      onChange={(event) => {
        const value = Number(event.currentTarget.value);
        setDraftValue(value);
        if (state.deviceSpeech && (pointerActive.current || keyboardActive.current)) {
          pendingDeviceSeek.current = value;
        } else {
          commit(value);
        }
      }}
      onPointerDown={(event) => {
        pointerActive.current = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }}
      onPointerUp={(event) => {
        pointerActive.current = false;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (pendingDeviceSeek.current !== null) commit(Number(event.currentTarget.value));
      }}
      onPointerCancel={(event) => {
        pointerActive.current = false;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (pendingDeviceSeek.current !== null) commit(Number(event.currentTarget.value));
      }}
      onKeyDown={(event) => {
        if (RANGE_KEYBOARD_KEYS.has(event.key)) keyboardActive.current = true;
      }}
      onKeyUp={(event) => {
        if (!keyboardActive.current || !RANGE_KEYBOARD_KEYS.has(event.key)) return;
        keyboardActive.current = false;
        if (pendingDeviceSeek.current !== null) commit(Number(event.currentTarget.value));
      }}
      onBlur={(event) => {
        pointerActive.current = false;
        keyboardActive.current = false;
        if (pendingDeviceSeek.current !== null) commit(Number(event.currentTarget.value));
      }}
      disabled={!durationKnown || state.phase === "loading"}
      aria-label={copy("audio.seek")}
      aria-valuetext={copy("audio.timelineValue", {
        current: formatTime(displayedPositionMs, lang),
        duration: formatTime(state.durationMs, lang),
      })}
      style={
        {
          "--audio-progress": `${displayedProgress * 100}%`,
          "--audio-buffered": `${Math.max(displayedProgress, state.buffered01 || 0) * 100}%`,
        } as CSSProperties
      }
    />
  );
}

function phaseAnnouncement(
  state: AudioPlaybackState,
  copy: ReturnType<typeof publicWebCopyForLang>,
) {
  if (state.phase === "loading") return copy("common.loadingAudio");
  if (state.phase === "error") return state.detail || copy("common.audio");
  if (state.buffering) return copy("audio.buffering");
  if (state.paused) return copy("audio.paused");
  return copy("common.nowPlaying");
}

type ExpandedAudioPlayerProps = {
  animated: boolean;
  copy: ReturnType<typeof publicWebCopyForLang>;
  durationKnown: boolean;
  lang: string;
  onClose: () => void;
  onRateChange: (rate: number) => void;
  onTogglePlayback: () => void;
  onVoiceChange: (voiceId: string) => void;
  playing: boolean;
  preferences: WebPreferences;
  progress: number;
  selectedRate: number;
  state: AudioPlaybackState;
  statusLabel: string;
};

function ExpandedAudioPlayer({
  animated,
  copy,
  durationKnown,
  lang,
  onClose,
  onRateChange,
  onTogglePlayback,
  onVoiceChange,
  playing,
  preferences,
  progress,
  selectedRate,
  state,
  statusLabel,
}: ExpandedAudioPlayerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useModalDialog<HTMLElement>({
    initialFocusRef: closeRef,
    onClose,
  });

  return createPortal(
    <div
      className="global-audio-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="global-audio-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="global-audio-dialog-title"
        aria-busy={state.phase === "loading" || Boolean(state.buffering)}
      >
        <div className="global-audio-dialog__glow" aria-hidden="true" />
        <header className="global-audio-dialog__header">
          <div className="global-audio-dialog__brand" aria-hidden="true">
            <BrandMark />
          </div>
          <div>
            <span>{copy("audio.premiumListening")}</span>
            <h2 id="global-audio-dialog-title">{state.label || copy("brief.eyebrow")}</h2>
            <p>{state.detail || copy("common.audio")}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="global-audio-dialog__close"
            onClick={onClose}
            aria-label={copy("audio.collapse")}
          >
            <PlayerIcon name="close" />
          </button>
        </header>

        <AudioWaveform active={animated} />

        <div className="global-audio-dialog__scrubber">
          <AudioScrubber
            copy={copy}
            durationKnown={durationKnown}
            lang={lang}
            progress={progress}
            state={state}
          />
          <div>
            <time>{formatTime(state.positionMs, lang)}</time>
            <span>{state.buffering ? copy("audio.buffering") : statusLabel}</span>
            <time>{formatTime(state.durationMs, lang)}</time>
          </div>
        </div>

        <div className="global-audio-dialog__transport">
          <button
            type="button"
            onClick={() => seekAudioPlaybackBy(-15)}
            disabled={!durationKnown}
            aria-label={copy("audio.rewind")}
          >
            <PlayerIcon name="back" />
          </button>
          <button
            type="button"
            className="global-audio-dialog__play"
            onClick={onTogglePlayback}
            disabled={state.phase === "loading"}
            aria-label={
              state.phase === "loading"
                ? copy("common.loadingAudio")
                : playing
                  ? copy("audio.pause")
                  : copy("audio.resume")
            }
          >
            {state.phase === "loading" || state.buffering ? (
              <span className="global-audio-player__spinner" aria-hidden="true" />
            ) : (
              <PlayerIcon name={playing ? "pause" : "play"} />
            )}
          </button>
          <button
            type="button"
            onClick={() => seekAudioPlaybackBy(15)}
            disabled={!durationKnown}
            aria-label={copy("audio.forward")}
          >
            <PlayerIcon name="forward" />
          </button>
        </div>

        <section className="global-audio-dialog__settings" aria-label={copy("audio.controls")}>
          <div className="global-audio-dialog__setting-head">
            <div>
              <span>{copy("audio.playbackSpeed")}</span>
              <strong>{rateLabel(selectedRate, lang)}</strong>
            </div>
          </div>
          <div
            className="global-audio-dialog__speed-options"
            role="group"
            aria-label={copy("audio.playbackSpeed")}
          >
            {AUDIO_PLAYBACK_SPEED_OPTIONS.map((rate) => (
              <button
                type="button"
                key={rate}
                className={Math.abs(selectedRate - rate) < 0.001 ? "is-selected" : ""}
                aria-pressed={Math.abs(selectedRate - rate) < 0.001}
                onClick={() => onRateChange(rate)}
              >
                {rateLabel(rate, lang)}
              </button>
            ))}
          </div>

          <label className="global-audio-dialog__voice">
            <span className="global-audio-dialog__voice-icon" aria-hidden="true">
              <PlayerIcon name="voice" />
            </span>
            <span>
              <strong>{copy("audio.narrator")}</strong>
              <small>{copy("audio.narratorFuture")}</small>
            </span>
            <select
              value={preferences.audioVoiceId}
              onChange={(event) => onVoiceChange(event.currentTarget.value)}
              aria-label={copy("audio.narrator")}
            >
              {audioVoiceOptionsForLanguage(lang).map((voice) => (
                <option key={voice.id} value={voice.id}>
                  {voice.id === "auto" ? copy("audio.autoVoice") : voice.label}
                </option>
              ))}
            </select>
          </label>
        </section>

        <footer className="global-audio-dialog__footer">
          <button
            type="button"
            onClick={() => downloadCurrentAudio()}
            disabled={!state.canDownload || state.phase !== "playing"}
          >
            <PlayerIcon name="download" />
            {copy("audio.download")}
          </button>
          <button type="button" onClick={stopAudioPlayback}>
            <PlayerIcon name="stop" />
            {copy("common.stop")}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

function DailyDefaultAudioDialog({
  offer,
}: {
  offer: NonNullable<AudioPlaybackState["dailyDefaultAudioOffer"]>;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const copy = publicWebCopyForLang(offer.languageCode);
  const loading = offer.status === "loading";
  const failed = offer.status === "error";
  const showSubscribeCta = offer.showSubscribeCta;
  const onClose = () => dismissDailyDefaultEditionAudio();
  const dialogRef = useModalDialog<HTMLElement>({ initialFocusRef: closeRef, onClose });

  return createPortal(
    <div
      className="global-audio-dialog-backdrop daily-default-audio-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="global-audio-dialog daily-default-audio-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="daily-default-audio-title"
        aria-describedby="daily-default-audio-description"
        aria-busy={loading}
      >
        <div className="global-audio-dialog__glow" aria-hidden="true" />
        <button
          ref={closeRef}
          type="button"
          className="global-audio-dialog__close daily-default-audio-dialog__close"
          onClick={onClose}
          aria-label={copy("audio.dailyDefault.notNow")}
        >
          <PlayerIcon name="close" />
        </button>
        <div className="daily-default-audio-dialog__mark" aria-hidden="true">
          <BrandMark />
        </div>
        <p className="daily-default-audio-dialog__eyebrow">
          {copy("audio.dailyDefault.eyebrow")}
        </p>
        <h2 id="daily-default-audio-title">{copy("audio.dailyDefault.title")}</h2>
        <p id="daily-default-audio-description" className="daily-default-audio-dialog__body">
          {copy(
            showSubscribeCta
              ? "audio.dailyDefault.freeBody"
              : "audio.dailyDefault.memberBody",
          )}
        </p>
        <div className="daily-default-audio-dialog__edition">
          <span aria-hidden="true">♪</span>
          <div>
            <strong>{copy("audio.dailyDefault.label")}</strong>
            <small>{copy("audio.dailyDefault.detail")}</small>
          </div>
        </div>
        <div className="daily-default-audio-dialog__status" aria-live="polite">
          {loading ? (
            <p role="status">
              <span className="global-audio-player__spinner" aria-hidden="true" />
              {copy("audio.dailyDefault.loading")}
            </p>
          ) : failed ? (
            <p role="alert">{copy("audio.dailyDefault.unavailable")}</p>
          ) : null}
        </div>
        <div className="daily-default-audio-dialog__actions">
          {showSubscribeCta && !loading ? (
            <Link
              className="daily-default-audio-dialog__primary"
              to="/app/settings/subscription"
              onClick={() => {
                trackDailyDefaultEditionSubscribe();
                onClose();
              }}
            >
              {copy("audio.dailyDefault.subscribe")}
            </Link>
          ) : null}
          <button
            type="button"
            className={showSubscribeCta ? "daily-default-audio-dialog__secondary" : "daily-default-audio-dialog__primary"}
            onClick={() => void continueWithDailyDefaultEditionAudio()}
            disabled={loading}
          >
            {copy(
              failed ? "audio.dailyDefault.retry" : "audio.dailyDefault.continue",
            )}
          </button>
          <button
            type="button"
            className={`daily-default-audio-dialog__secondary${showSubscribeCta ? "" : " daily-default-audio-dialog__secondary--wide"}`}
            onClick={onClose}
          >
            {copy("audio.dailyDefault.notNow")}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

export function GlobalAudioPlayer({ lang }: { lang: string }) {
  const copy = publicWebCopyForLang(lang);
  const [state, setState] = useState<AudioPlaybackState>(() => getAudioPlaybackState());
  const [preferences, setPreferences] = useState<WebPreferences>(() => loadPreferences());
  const [expanded, setExpanded] = useState(false);
  const errorDismissRef = useRef<HTMLButtonElement>(null);
  const progress = playbackProgress(state);
  const durationKnown = (state.durationMs || 0) > 0;
  const playing = state.phase === "playing" && !state.paused;
  const animated = playing || state.phase === "loading" || Boolean(state.buffering);
  const selectedRate = state.playbackRate || preferences.audioPlaybackSpeed || 1;
  const dailyDefaultAudioOffer = state.dailyDefaultAudioOffer;

  useEffect(() => {
    const update = () => setState(getAudioPlaybackState());
    const removePlaybackListener = onAudioPlaybackChanged(update);
    const removeTimelineListener = onAudioPlaybackTimelineChanged(update);
    return () => {
      removePlaybackListener();
      removeTimelineListener();
    };
  }, []);

  useEffect(() => {
    const update = () => {
      const current = loadPreferences();
      const compatibleVoiceId = compatibleAudioVoiceId(current.audioVoiceId, lang);
      const compatible =
        compatibleVoiceId === current.audioVoiceId
          ? current
          : { ...current, audioVoiceId: compatibleVoiceId };
      setPreferences(compatible);
      setAudioPlaybackRate(current.audioPlaybackSpeed);
      if (compatible !== current) savePreferences(compatible);
    };
    update();
    return onPreferencesChanged(update);
  }, [lang]);

  useEffect(() => {
    if (state.phase === "error") {
      if (!expanded) return;
      setExpanded(false);
      if (state.dailyDefaultAudioOffer) return;
      window.requestAnimationFrame(() => {
        const focusTarget = errorDismissRef.current ?? document.getElementById("main-content");
        if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
      });
      return;
    }
    if (state.phase !== "idle" || !expanded) return;
    setExpanded(false);
    window.requestAnimationFrame(() => {
      const mainContent = document.getElementById("main-content");
      if (mainContent instanceof HTMLElement) mainContent.focus({ preventScroll: true });
    });
  }, [expanded, state.dailyDefaultAudioOffer, state.phase]);

  function togglePlayback() {
    if (state.phase !== "playing") return;
    if (state.paused) void resumeAudioPlayback();
    else pauseAudioPlayback();
  }

  function selectRate(rate: number) {
    const applied = setAudioPlaybackRate(rate);
    const next = { ...loadPreferences(), audioPlaybackSpeed: applied };
    setPreferences(next);
    savePreferences(next);
  }

  function selectVoice(voiceId: string) {
    const next = { ...loadPreferences(), audioVoiceId: voiceId };
    setPreferences(next);
    savePreferences(next);
  }

  if (state.phase === "idle") return null;

  const statusLabel =
    state.phase === "loading"
      ? copy("common.loadingAudio")
      : state.phase === "error"
        ? copy("common.audio")
        : state.buffering
          ? copy("audio.buffering")
          : state.paused
            ? copy("audio.paused")
            : copy("common.nowPlaying");

  return (
    <>
      {!dailyDefaultAudioOffer ? (
        <aside
        className={`global-audio-player global-audio-player--${state.phase}${expanded ? " is-expanded" : ""}`}
        aria-label={copy("layout.audioPlayer")}
        aria-busy={state.phase === "loading" || Boolean(state.buffering)}
        aria-hidden={expanded || undefined}
        inert={expanded || undefined}
      >
        <span className="global-audio-player__announcement" role="status">
          {phaseAnnouncement(state, copy)}
        </span>
        <div className="global-audio-player__glow" aria-hidden="true" />
        <div className="global-audio-player__mark">
          <AudioWaveform active={animated} compact />
        </div>

        <button
          type="button"
          className="global-audio-player__identity"
          onClick={() => state.phase !== "error" && setExpanded(true)}
          disabled={state.phase === "error"}
          aria-label={`${copy("audio.expand")}: ${state.label || copy("brief.eyebrow")}`}
        >
          <span>{statusLabel}</span>
          <strong>{state.label || copy("brief.eyebrow")}</strong>
          <small>{state.detail || copy("audio.premiumListening")}</small>
        </button>

        {state.phase === "error" ? (
          <div className="global-audio-player__error-actions">
            {state.deviceSpeechReady ? (
              <button type="button" onClick={retryDeviceSpeechPlayback}>
                {copy("audio.useDeviceVoice")}
              </button>
            ) : !dailyDefaultAudioOffer &&
              shouldShowAudioUpgradeCta(state.quotaKind, state.showSubscribeCta) ? (
              <Link to="/app/settings/subscription">{copy("common.viewPlans")}</Link>
            ) : null}
            <button ref={errorDismissRef} type="button" onClick={stopAudioPlayback}>
              {copy("common.dismiss")}
            </button>
          </div>
        ) : (
          <>
            <div className="global-audio-player__controls">
              <button
                type="button"
                className="global-audio-player__speed"
                onClick={() => setExpanded(true)}
                aria-label={`${copy("audio.playbackSpeed")}: ${rateLabel(selectedRate, lang)}`}
              >
                {rateLabel(selectedRate, lang)}
              </button>
              <button
                type="button"
                className="global-audio-player__primary"
                onClick={togglePlayback}
                disabled={state.phase === "loading"}
                aria-label={
                  state.phase === "loading"
                    ? copy("common.loadingAudio")
                    : playing
                      ? copy("audio.pause")
                      : copy("audio.resume")
                }
              >
                {state.phase === "loading" || state.buffering ? (
                  <span className="global-audio-player__spinner" aria-hidden="true" />
                ) : (
                  <PlayerIcon name={playing ? "pause" : "play"} />
                )}
              </button>
              <button
                type="button"
                className="global-audio-player__close"
                onClick={stopAudioPlayback}
                aria-label={copy("audio.stopClose")}
              >
                <PlayerIcon name="close" />
              </button>
            </div>

            <div className="global-audio-player__timeline">
              <AudioScrubber
                copy={copy}
                durationKnown={durationKnown}
                lang={lang}
                progress={progress}
                state={state}
              />
              <span>{formatTime(state.positionMs, lang)}</span>
              <span>
                {durationKnown
                  ? `−${formatTime((state.durationMs || 0) - (state.positionMs || 0), lang)}`
                  : "—:—"}
              </span>
            </div>
          </>
        )}
        </aside>
      ) : null}

      {expanded && !dailyDefaultAudioOffer ? (
        <ExpandedAudioPlayer
          animated={animated}
          copy={copy}
          durationKnown={durationKnown}
          lang={lang}
          onClose={() => setExpanded(false)}
          onRateChange={selectRate}
          onTogglePlayback={togglePlayback}
          onVoiceChange={selectVoice}
          playing={playing}
          preferences={preferences}
          progress={progress}
          selectedRate={selectedRate}
          state={state}
          statusLabel={statusLabel}
        />
      ) : null}
      {dailyDefaultAudioOffer ? (
        <DailyDefaultAudioDialog offer={dailyDefaultAudioOffer} />
      ) : null}
    </>
  );
}
